import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Mic,
  Square,
  Globe,
  AlertTriangle,
  GraduationCap,
  CheckCircle2,
} from 'lucide-react';
import { Orb } from '../../ui/orb';
import { Button } from '../../ui/atoms/Button';
import { Tag } from '../../ui/atoms/Tag';
import { SegmentedButton } from '../../ui/atoms/SegmentedButton';
import { useCoachingSession } from '../../../hooks/useCoachingSession';
import { LearningResources } from './LearningResources';
import {
  CareerAdvisorAvailability,
  CoachingEvaluation,
  evaluateCoaching,
} from '../../../services/career-advisor.service';
interface CareerAdvisorStageProps {
  invitationToken: string;
  availability: CareerAdvisorAvailability;
  /** Notifies the parent so it can refetch quota after a session completes. */
  onSessionFinished?: () => void;
}

const ORB_COLORS: [string, string] = ['#E5CBF6', '#D9ADFF'];
const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
] as const;

const formatTime = (seconds: number): string => {
  const safe = Math.max(0, seconds);
  return `${Math.floor(safe / 60)}:${(safe % 60).toString().padStart(2, '0')}`;
};

type AgentStateLabelKey =
  | 'career_advisor.stage.agent_state.listening'
  | 'career_advisor.stage.agent_state.talking'
  | 'career_advisor.stage.agent_state.thinking'
  | 'career_advisor.stage.agent_state.ready';

const agentStateKey = (s: string | null): AgentStateLabelKey => {
  switch (s) {
    case 'listening':
      return 'career_advisor.stage.agent_state.listening';
    case 'talking':
      return 'career_advisor.stage.agent_state.talking';
    case 'thinking':
      return 'career_advisor.stage.agent_state.thinking';
    default:
      return 'career_advisor.stage.agent_state.ready';
  }
};

/** The framed voice orb — clipped to a circle so it reads as a contained core. */
const OrbViewport = ({
  agentState,
  getInputVolume,
  getOutputVolume,
}: {
  agentState: string | null;
  getInputVolume: () => number;
  getOutputVolume: () => number;
}) => (
  <div className="relative h-32 w-32 shrink-0 sm:h-36 sm:w-36">
    <div
      aria-hidden
      className="absolute inset-0 rounded-full bg-purple-soft/60 blur-xl"
    />
    <div className="relative h-full w-full overflow-hidden rounded-full bg-purple-soft/25 ring-1 ring-light-lila/70">
      <Orb
        agentState={agentState as never}
        colors={ORB_COLORS}
        seed={42}
        getInputVolume={getInputVolume}
        getOutputVolume={getOutputVolume}
      />
    </div>
  </div>
);

/** Candidate-facing recap of the prep session — only the warm summary the
 *  coach wrote for them. No scoring, no strengths/improvements columns:
 *  the prep is coaching, not grading, and we don't want a second judgement
 *  surface to interfere with the real interview that follows. */
const PrepRecap = ({ evaluation }: { evaluation: CoachingEvaluation }) => (
  <div className="px-5 py-5">
    <p className="text-sm leading-relaxed text-dark-invirtus font-body">
      {evaluation.summary}
    </p>
  </div>
);

export const CareerAdvisorStage = ({
  invitationToken,
  availability,
  onSessionFinished,
}: CareerAdvisorStageProps) => {
  const { t } = useTranslation('interview');
  const {
    state,
    startSession,
    endSession,
    getInputVolume,
    getOutputVolume,
  } = useCoachingSession();

  const [language, setLanguage] = useState<string>('en');
  const [evaluation, setEvaluation] = useState<CoachingEvaluation | null>(null);
  const [evaluating, setEvaluating] = useState(false);
  const evaluatedRef = useRef(false);
  const onSessionFinishedRef = useRef(onSessionFinished);
  onSessionFinishedRef.current = onSessionFinished;

  useEffect(() => {
    if (state.status !== 'done' || evaluatedRef.current) return;
    evaluatedRef.current = true;
    if (state.transcript.length >= 2) {
      setEvaluating(true);
      evaluateCoaching(invitationToken, state.transcript)
        .then(setEvaluation)
        .catch(() => setEvaluation(null))
        .finally(() => {
          setEvaluating(false);
          // Tell the parent to refresh quota — backend just persisted a row
          // so sessions_remaining will be decremented.
          onSessionFinishedRef.current?.();
        });
    } else {
      // Session ended too short to evaluate (no transcript); still ask the
      // parent to refresh so the counter stays accurate.
      onSessionFinishedRef.current?.();
    }
  }, [state.status, state.transcript, invitationToken]);

  // One conversation only — the candidate has a session left only if they
  // haven't used theirs yet. After the first run we land permanently on the
  // "done" screen with the recap + resources, no retry option.
  const hasSessionLeft = availability.sessions_remaining > 0;

  // Shared header — same chrome across every state so the candidate always
  // sees the same "Interview Coach" framing through the conversation and its
  // wrap-up.
  const renderHeader = (variant: 'practice' | 'done') => (
    <div className="flex items-start gap-3 border-b border-light-lila/50 px-5 py-3.5">
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
          variant === 'done'
            ? 'bg-purple text-light'
            : 'bg-purple-soft/40 text-purple'
        }`}
      >
        {variant === 'done' ? (
          <CheckCircle2 className="h-[18px] w-[18px]" />
        ) : (
          <GraduationCap className="h-[18px] w-[18px]" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h4 className="font-heading text-sm font-semibold text-dark-invirtus">
            {t('career_advisor.stage.card_title')}
          </h4>
          {variant === 'done' && (
            <Tag intent="primary" variant="solid" className="uppercase">
              {t('career_advisor.stage.badge_done')}
            </Tag>
          )}
        </div>
        <p className="mt-0.5 text-xs leading-relaxed text-dark-invirtus/55 font-body">
          {variant === 'done'
            ? t('career_advisor.stage.header_subtitle_done')
            : t('career_advisor.stage.header_subtitle_practice')}
        </p>
      </div>
    </div>
  );

  // ----- ALL SESSIONS USED (entered the prep area but quota exhausted) --
  // Reached either by finishing the last session, OR by re-landing on the
  // page after already finishing all sessions on a previous visit.
  const allSessionsDone =
    !hasSessionLeft && state.status !== 'connecting' && state.status !== 'active' && state.status !== 'ending';

  if (allSessionsDone && state.status !== 'done') {
    // Pristine "you're done" landing (no fresh session results to show).
    return (
      <div className="overflow-hidden rounded-2xl border border-light-lila/60 bg-light">
        {renderHeader('done')}
        <div className="px-5 py-5">
          <p className="text-sm leading-relaxed text-dark-invirtus font-body">
            {t('career_advisor.stage.all_sessions_done_message')}
          </p>
        </div>
        <div className="border-t border-light-lila/50">
          <LearningResources invitationToken={invitationToken} />
        </div>
      </div>
    );
  }

  // ----- RESULTS (a session just finished) ------------------------------
  if (state.status === 'done') {
    if (evaluating) {
      return (
        <div className="overflow-hidden rounded-2xl border border-light-lila/60 bg-light">
          {renderHeader('practice')}
          <div className="flex h-48 flex-col items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-purple border-t-transparent" />
            <p className="mt-3 text-sm text-dark-invirtus/55 font-body">
              {t('career_advisor.stage.evaluating')}
            </p>
          </div>
        </div>
      );
    }
    if (evaluation) {
      return (
        <div className="overflow-hidden rounded-2xl border border-light-lila/60 bg-light">
          {renderHeader('done')}
          <div className="border-b border-light-lila/50 px-5 py-3">
            <p className="text-xs text-dark-invirtus/55 font-body">
              <span className="font-semibold text-dark-invirtus">
                {t('career_advisor.stage.recap_label')}
              </span>
              {t('career_advisor.stage.recap_suffix')}
            </p>
          </div>
          <PrepRecap evaluation={evaluation} />
          <div className="border-t border-light-lila/50">
            <LearningResources invitationToken={invitationToken} />
          </div>
        </div>
      );
    }
    // Session ended without enough transcript to evaluate.
    return (
      <div className="overflow-hidden rounded-2xl border border-light-lila/60 bg-light">
        {renderHeader('done')}
        <div className="flex h-48 flex-col items-center justify-center text-center px-6">
          <p className="font-heading text-base font-semibold text-dark-invirtus">
            {t('career_advisor.stage.conversation_wrapped_up')}
          </p>
          <p className="mt-1 max-w-sm text-sm text-dark-invirtus/55 font-body">
            {t('career_advisor.stage.conversation_wrapped_up_desc')}
          </p>
        </div>
        <div className="border-t border-light-lila/50">
          <LearningResources invitationToken={invitationToken} />
        </div>
      </div>
    );
  }

  // ----- SESSION (idle / connecting / active / ending / error) ----------
  const isLive = state.status === 'active' || state.status === 'ending';

  return (
    <div className="overflow-hidden rounded-2xl border border-light-lila/60 bg-light">
      {renderHeader('practice')}

      {/* One tight row: orb on the left, everything else beside it */}
      <div className="flex items-center gap-5 px-6 py-6 sm:gap-7">
        <OrbViewport
          agentState={state.agentState}
          getInputVolume={getInputVolume}
          getOutputVolume={getOutputVolume}
        />

        <div className="flex min-w-0 flex-1 flex-col items-start gap-3">
          {isLive ? (
            <>
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-purple" />
                <span className="text-sm font-medium text-dark-invirtus/70 font-body">
                  {t(agentStateKey(state.agentState))}
                </span>
              </div>
              <span className="font-mono text-4xl font-bold tabular-nums leading-none text-dark-invirtus">
                {formatTime(state.secondsRemaining)}
              </span>
              <Button intent="danger" onClick={endSession} className="gap-2">
                <Square className="h-4 w-4" />
                {t('career_advisor.stage.end_conversation')}
              </Button>
            </>
          ) : state.status === 'connecting' ? (
            <div className="flex items-center gap-2 text-dark-invirtus/45">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-purple border-t-transparent" />
              <span className="text-sm font-body">{t('career_advisor.stage.connecting')}</span>
            </div>
          ) : (
            <>
              <h5 className="font-heading text-base font-semibold text-dark-invirtus">
                {t('career_advisor.stage.ask_title')}
              </h5>
              <p className="text-sm leading-relaxed text-dark-invirtus/60 font-body">
                {t('career_advisor.stage.ask_description')}
              </p>
              <div className="mt-1 flex w-full flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5 text-dark-invirtus/40" aria-hidden />
                  <SegmentedButton
                    aria-label={t('career_advisor.stage.language_aria_label', {
                      defaultValue: 'Session language',
                    })}
                    size="sm"
                    appearance="filled"
                    intent="secondary"
                    value={language}
                    onValueChange={setLanguage}
                    options={LANGUAGES.map((lang) => ({
                      value: lang.code,
                      label: lang.label,
                    }))}
                  />
                </div>
                <Button
                  intent="primary"
                  onClick={() => startSession(invitationToken, language)}
                  className="gap-2"
                  disabled={!hasSessionLeft}
                >
                  <Mic className="h-4 w-4" />
                  {t('career_advisor.stage.talk_button')}
                </Button>
              </div>
              {state.error && (
                <div className="flex items-start gap-2 rounded-lg bg-red-soft/40 p-2.5">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red" />
                  <span className="text-xs text-dark-invirtus font-body">
                    {t(state.error)}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
